import { Component, ComponentChild } from 'preact'
import Axios from 'axios'
import { Group } from '../groups-destination/GroupOverview.sync'
import { arrayMove, SortableContainer, SortableElement } from 'react-sortable-hoc'
import InputGroupWithIcons from '../../control-components/ui-components/InputGroupWithIcons'
import Icon from '../assets/index'
import DragHandle from '../control/DragHandle'
import { GroupsWidgetConfig } from './index.sync'

interface NewData {
  groups: Group[]
}

interface GroupsWidgetProps {
  config: GroupsWidgetConfig
  onChange: (newData: NewData) => void
}

interface State {
  filterText: string
  inputInFocus: boolean
  allGroups: Group[]
  selectedGroups: Group[]
  filteredGroups: Group[]
  remainingGroupsAfterSelection: Group[]
}

const SortableList = SortableContainer(({ children }: { children: ComponentChild }) => (
  <ul className='sortable-groups-list'>{children}</ul>
))
const SortableItem = SortableElement(({ children }: { children: ComponentChild }) => (
  <li className='sortable-group'>{children}</li>
))

export class GroupsWidgetConfigurator extends Component<GroupsWidgetProps, State> {
  constructor (props: GroupsWidgetProps) {
    super(props)

    this.state = {
      filterText: '',
      inputInFocus: false,
      allGroups: [],
      selectedGroups: props.config.groups as Group[] || [],
      filteredGroups: [],
      remainingGroupsAfterSelection: []
    }

    this.setDropdownItems = this.setDropdownItems.bind(this)
    this.setSelectedGroups = this.setSelectedGroups.bind(this)
    this.reorderGroups = this.reorderGroups.bind(this)
    this.onChangeSend = this.onChangeSend.bind(this)
    this.removeSelectedGroup = this.removeSelectedGroup.bind(this)
  }

  setSelectedGroups (selectedGroup: Group) {
    const selectedGroupIndex = this.state.remainingGroupsAfterSelection.findIndex(group => selectedGroup.id === group.id)
    const newRemainingGroups = this.state.remainingGroupsAfterSelection.concat([])
    newRemainingGroups.splice(selectedGroupIndex, 1)
    this.setState({
      selectedGroups: this.state.selectedGroups.concat(selectedGroup),
      remainingGroupsAfterSelection: newRemainingGroups,
      filterText: ''
    }, this.onChangeSend)
  }

  removeSelectedGroup (idx: number, groupToDelete: Group) {
    const newSelectedGroups = this.state.selectedGroups.concat([])
    newSelectedGroups.splice(idx, 1)
    const newRemainingGroups = this.state.remainingGroupsAfterSelection.concat([])
    newRemainingGroups.push(groupToDelete)
    this.setState({ selectedGroups: newSelectedGroups, remainingGroupsAfterSelection: newRemainingGroups }, this.onChangeSend)
  }

  onChangeSend () {
    const newData: NewData = {
      groups: this.state.selectedGroups
    }

    this.props.onChange(newData)
  }

  setDropdownItems (filterValue: string) {
    if (/^https?/.test(filterValue)) {
      const filteredGroups = this.state.remainingGroupsAfterSelection.filter(group => group.url.toLowerCase().includes(filterValue.toLowerCase()))
      this.setState({ filteredGroups: filteredGroups })
    } else {
      const filteredGroups = this.state.remainingGroupsAfterSelection.filter(group => group.title.toLowerCase().includes(filterValue.toLowerCase()))
      this.setState({ filteredGroups: filteredGroups })
    }
  }

  reorderGroups (result: {oldIndex: number, newIndex: number}) {
    const newOrder = arrayMove(this.state.selectedGroups, result.oldIndex, result.newIndex)
    this.setState({ selectedGroups: newOrder }, this.onChangeSend)
  }

  async componentDidMount () {
    const res = await Axios.get('/getGroups')
    if (res.data) {
      const allGroups = res.data.myGroups.map((myGroup: Group) => { myGroup.isUserMember = true; return myGroup })
        .concat(res.data.otherGroups)

      const remainingGroupsAfterSelection = allGroups.concat([])

      for (const selectedGroup of this.props.config.groups) {
        const idx = remainingGroupsAfterSelection.findIndex((group: Group) => group.id === selectedGroup.id)
        remainingGroupsAfterSelection.splice(idx, 1)
      }

      this.setState({ allGroups: allGroups, remainingGroupsAfterSelection: remainingGroupsAfterSelection })
    }
  }

  render () {
    const { selectedGroups, filteredGroups, filterText, inputInFocus } = this.state
    return (
      <div className='groups-widget__configurator'>
        <label className='a-text-field--label'>Groups</label>
        <SortableList
          useDragHandle
          onSortEnd={this.reorderGroups}
        >
          {
                      selectedGroups.map((selectedGroup, idx) => (
                        <SortableItem index={idx} key={selectedGroup.id}>
                          <div>
                            <DragHandle />
                            <div className='sortable-group_title'>{selectedGroup.title}</div>
                            <div
                              className='sortable-group_delete-icon'
                              onClick={() => this.removeSelectedGroup(idx, selectedGroup)}
                            >
                              <Icon color='#2B3346' icon='trash' />
                            </div>
                          </div>
                        </SortableItem>
                      ))
                    }
          <div className='groups-widget-add-group-wrapper'>
            <InputGroupWithIcons
              attributes={{
                type: 'text',
                placeholder: 'Search for groups or enter a group URL',
                value: filterText
              }}
              className='groups-widget-add-input-wrapper'
              preIcon={<Icon color='#6B707E' icon='add' />}
              onChange={(newValue: string) => {
                this.setState({ filterText: newValue }, () => this.setDropdownItems(newValue))
              }}
              onFocus={() => this.setState({ inputInFocus: true }, () => this.setDropdownItems(filterText))}
              onBlur={() => setTimeout(() => {
                this.setState({ inputInFocus: false })
              }, 100)}
            />
            {
                        inputInFocus
                          ? (
                            <ul className='groups-widget-dropdown-list'>
                              {
                                    filteredGroups.length
                                      ? (
                                          filteredGroups.map((filteredGroup: Group) => (
                                            <li
                                              key={filteredGroup.id}
                                              className='groups-widget-dropdown-list_item'
                                              onMouseDown={() => this.setSelectedGroups(filteredGroup)}
                                            >{filteredGroup.title}</li>
                                          ))
                                        )
                                      : <li className='groups-widget-dropdown-list_item'>No Match</li>
                                  }
                            </ul>
                            )
                          : null
                      }
          </div>
        </SortableList>
      </div>
    )
  }
}

export default GroupsWidgetConfigurator
